import React, { useEffect, useRef, useState } from "react";

// Timeline/endpoint visual constants (reuse same values as SummaryScreen)
const BOX_SIZE = 10;
const BOX_HALF = BOX_SIZE / 2.5;
const LINE_WIDTH = 2;
const LINE_COLOR = "#211F59";

function PickupDetails({
  pickUpPersonName,
  pickUpPersonPhone,
  pickupLocation,
  pickUpInstructions,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [maxH, setMaxH] = useState("0px");

  useEffect(() => {
    if (!ref.current) return;
    if (open) setMaxH(`${ref.current.scrollHeight}px`);
    else setMaxH("0px");
  }, [open]);

  useEffect(() => {
    const onResize = () => {
      if (open && ref.current) setMaxH(`${ref.current.scrollHeight}px`);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  return (
    <>
      <div
        ref={ref}
        className={`${open ? "h-auto" : "h-0"} overflow-hidden transition-all duration-300`}
        style={{ maxHeight: maxH }}
      >
        <div>
          {pickupLocation ? (
            <div className="text-[#211F59] text-[14px] font-normal mt-[8px]">
              {pickupLocation}
            </div>
          ) : null}

          {pickUpPersonName ? (
            <div className="text-[#211F59] text-[14px] mt-2.5">
              {pickUpPersonName}
            </div>
          ) : null}

          {pickUpPersonPhone ? (
            <div className="text-[#211F59] text-[14px]">
              {pickUpPersonPhone}
            </div>
          ) : null}

          {pickUpInstructions ? (
            <div className="text-[#211F59] text-[13px] mt-2">
              <strong>Instructions : </strong>
              {pickUpInstructions}
            </div>
          ) : null}

          {/* If no pickup fields are provided, show nothing (render only provided fields) */}
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-2">
        <button
          onClick={() => setOpen((s) => !s)}
          aria-expanded={open}
          className="flex items-center focus:outline-none"
        >
          <img
            src="/Vision Agent Icons/Back Arrow/ChevronLightPurple.svg"
            alt={open ? "collapse details" : "expand details"}
            className={`w-3 h-3 transform transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"
              }`}
          />
        </button>
        <button
          onClick={() => setOpen((s) => !s)}
          className="text-[#7876A1] text-[12px] font-medium focus:outline-none"
        >
          {open ? "Hide details" : "Show details"}
        </button>
      </div>
    </>
  );
}

function DropoffDetails({
  dropOffPersonName,
  dropOffPersonPhone,
  dropoffLocation,
  dropOffInstructions,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [maxH, setMaxH] = useState("0px");

  useEffect(() => {
    if (!ref.current) return;
    if (open) setMaxH(`${ref.current.scrollHeight}px`);
    else setMaxH("0px");
  }, [open]);

  useEffect(() => {
    const onResize = () => {
      if (open && ref.current) setMaxH(`${ref.current.scrollHeight}px`);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  return (
    <>
      <div
        ref={ref}
        className={`${open ? "h-auto" : "h-0"
          } overflow-hidden transition-all duration-300`}
        style={{ maxHeight: maxH }}
      >
        <div>
          {dropoffLocation ? (
            <div className="text-[#211F59] text-[14px] font-normal mt-[8px]">
              {dropoffLocation}
            </div>
          ) : null}

          {dropOffPersonName ? (
            <div className="text-[#211F59] text-[14px] mt-2.5">
              {dropOffPersonName}
            </div>
          ) : null}

          {dropOffPersonPhone ? (
            <div className="text-[#211F59] text-[14px]">
              {dropOffPersonPhone}
            </div>
          ) : null}

          {dropOffInstructions ? (
            <div className="text-[#211F59] text-[13px] mt-2">
              <strong>Instructions : </strong>
              {dropOffInstructions}
            </div>
          ) : null}

          {/* If no dropoff fields are provided, show a subtle placeholder */}
          {!dropoffLocation &&
            !dropOffPersonName &&
            !dropOffPersonPhone &&
            !dropOffInstructions ? (
            <div className="text-[#9A98B8] text-[13px] mt-2">
              Drop-off details not provided
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-2">
        <button
          onClick={() => setOpen((s) => !s)}
          aria-expanded={open}
          className="flex items-center focus:outline-none"
        >
          <img
            src="/Vision Agent Icons/Back Arrow/ChevronLightPurple.svg"
            alt={open ? "collapse details" : "expand details"}
            className={`w-3 h-3 transform transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"
              }`}
          />
        </button>
        <button
          onClick={() => setOpen((s) => !s)}
          className="text-[#7876A1] text-[12px] font-medium focus:outline-none"
        >
          {open ? "Hide details" : "Show details"}
        </button>
      </div>
    </>
  );
}

function StopDetails({ stopIndex, stop }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [maxH, setMaxH] = useState("0px");

  useEffect(() => {
    if (!ref.current) return;
    if (open) setMaxH(`${ref.current.scrollHeight}px`);
    else setMaxH("0px");
  }, [open]);

  useEffect(() => {
    const onResize = () => {
      if (open && ref.current) setMaxH(`${ref.current.scrollHeight}px`);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);



  return (
    <>
      <div
        ref={ref}
        className={`${open ? "h-auto" : "h-0"} overflow-hidden transition-all duration-300`}
        style={{ maxHeight: maxH }}
      >
        <div>
          {stop?.stop ? (
            <div className="text-[#211F59] text-[14px] font-normal mt-[8px]">{stop.stop}</div>
          ) : null}

          {stop?.personName ? (
            <div className="text-[#211F59] text-[14px] mt-2.5">{stop.personName}</div>
          ) : null}

          {stop?.personPhone ? (
            <div className="text-[#211F59] text-[14px]">{stop.personPhone}</div>
          ) : null}

          {stop?.instructions ? (
            <div className="text-[#211F59] text-[13px] mt-2">
              <strong>Instructions : </strong>
              {stop.instructions}
            </div>
          ) : null}

          {/* Do not render a "not provided" placeholder; only show provided stop fields */}
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-2">
        <button onClick={() => setOpen((s) => !s)} aria-expanded={open} className="flex items-center focus:outline-none">
          <img
            src="/Vision Agent Icons/Back Arrow/ChevronLightPurple.svg"
            alt={open ? "collapse details" : "expand details"}
            className={`w-3 h-3 transform transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`}
          />
        </button>
        <button onClick={() => setOpen((s) => !s)} className="text-[#7876A1] text-[12px] font-medium focus:outline-none">
          {open ? "Hide details" : "Show details"}
        </button>
      </div>
    </>
  );
}

const PickupDropoffSection = ({
  dropOffPersonName,
  dropOffPersonPhone,
  pickUpPersonName,
  pickUpPersonPhone,
  pickupLocation,
  dropoffLocation,
  stops,
  handleUpdateBookingObject,
  pickUpInstructions,
  dropOffInstructions,
  showEditActions = true,
  showBottomDivider = true,
}) => {
  const timelineRef = useRef(null);
  const topDotRef = useRef(null);
  const bottomDotRef = useRef(null);
  const [lineStyle, setLineStyle] = useState({ left: 0, top: 0, height: 0 });

  useEffect(() => {
    const computeLine = () => {
      if (!timelineRef.current || !topDotRef.current || !bottomDotRef.current)
        return;
      const wrapperRect = timelineRef.current.getBoundingClientRect();
      const topRect = topDotRef.current.getBoundingClientRect();
      const bottomRect = bottomDotRef.current.getBoundingClientRect();

      const left = topRect.left - wrapperRect.left + topRect.width / 2.4;
      const top = topRect.top - wrapperRect.top + topRect.height / 1;
      const bottomCenter =
        bottomRect.top - wrapperRect.top + bottomRect.height / 1;
      const height = Math.max(0, bottomCenter - top);

      setLineStyle({ left, top, height });
    };

    computeLine();
    const onResize = () => computeLine();
    window.addEventListener("resize", onResize);

    const ro = new ResizeObserver(() => {
      try {
        window.requestAnimationFrame(() => computeLine());
      } catch (e) {
        setTimeout(() => computeLine(), 0);
      }
    });
    if (timelineRef.current) ro.observe(timelineRef.current);
    if (topDotRef.current) ro.observe(topDotRef.current);
    if (bottomDotRef.current) ro.observe(bottomDotRef.current);

    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="mb-4 relative" ref={timelineRef}>
      {/* Render the timeline and endpoint squares only when a dropoff location exists */}
      {dropoffLocation && (
        <>
          <div
            style={{
              position: "absolute",
              left: `${lineStyle.left}px`,
              top: `${lineStyle.top}px`,
              height: `${lineStyle.height}px`,
              width: `${LINE_WIDTH}px`,
              backgroundColor: LINE_COLOR,
              borderRadius: "1px",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />

          <div
            style={{
              position: "absolute",
              left: `${lineStyle.left - BOX_HALF}px`,
              top: `${lineStyle.top - BOX_HALF}px`,
              width: `${BOX_SIZE}px`,
              height: `${BOX_SIZE}px`,
              backgroundColor: LINE_COLOR,
              pointerEvents: "none",
              zIndex: 1,
            }}
          />

          <div
            style={{
              position: "absolute",
              left: `${lineStyle.left - BOX_HALF}px`,
              top: `${lineStyle.top + lineStyle.height - BOX_HALF}px`,
              width: `${BOX_SIZE}px`,
              height: `${BOX_SIZE}px`,
              backgroundColor: LINE_COLOR,
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        </>
      )}

      <div className="flex items-start">
        <div className="w-6 flex flex-col items-center">
          <div ref={topDotRef} className="w-[10px] h-[10px] invisible" />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-x-[6px]">
            <div className="text-[#211F59] text-[16px] font-bold">
              Pick-up info
            </div>
            {showEditActions && (
              <img
                src="/images/NewUiIcons/Edit.svg"
                alt="edit"
                className="w-[23px] h-[23px] cursor-pointer"
                onClick={() => handleUpdateBookingObject("tripStatus", "locations", "EditBookingFromSummary")}
              />
            )}
          </div>

          <PickupDetails
            pickUpPersonName={pickUpPersonName}
            pickUpPersonPhone={pickUpPersonPhone}
            pickupLocation={pickupLocation}
            pickUpInstructions={pickUpInstructions}
          />
        </div>
      </div>

      {/* Stops (render between pickup and drop-off) */}
      {stops?.map((s, i) => (
        <div className="flex items-start mt-4" key={`summary-stop-${i}`}>
          <div className="w-6 flex flex-col items-center">
            <div className="w-[10px] h-[10px] invisible" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-x-[6px]">
              <div className="text-[#211F59] text-[16px] font-bold">Pick-up info {i + 2}</div>
              {showEditActions && (
                <img
                  src="/images/NewUiIcons/Edit.svg"
                  alt="edit"
                  className="w-[23px] h-[23px] cursor-pointer"
                  onClick={() => handleUpdateBookingObject("tripStatus", "locations", "EditBookingFromSummary")}
                />
              )}
            </div>
            <StopDetails stopIndex={i} stop={s} />
          </div>
        </div>
      ))}

      {dropoffLocation && (
        <div className="flex items-start mt-4">
          <div className="w-6 flex flex-col items-center">
            <div
              ref={bottomDotRef}
              className="w-[10px] h-[10px] invisible mt-auto"
            />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-x-[6px]">
              <div className="text-[#211F59] text-[16px] font-bold">
                Drop-off info
              </div>
              {showEditActions && (
                <img
                  src="/images/NewUiIcons/Edit.svg"
                  alt="edit"
                  className="w-[23px] h-[23px] cursor-pointer"
                  onClick={() => handleUpdateBookingObject("tripStatus", "locations", "EditBookingFromSummary")}
                />
              )}
            </div>

            <DropoffDetails
              dropOffPersonName={dropOffPersonName}
              dropOffPersonPhone={dropOffPersonPhone}
              dropoffLocation={dropoffLocation}
              dropOffInstructions={dropOffInstructions}
            />
          </div>
        </div>
      )}

      {showBottomDivider && (
        <div
          style={{
            borderTop: "1px solid #7876A1",
            height: 0,
            marginTop: "18px",
            marginBottom: "23px",
          }}
        />
      )}

    </div>
  );
};

export default PickupDropoffSection;
